/* eslint-disable react/jsx-no-bind */
import { useMemo } from "react";

import { List, ListContent, ListHeader, ListItem } from "../List";
import { useGuides } from "./useGuide";
import { HighlightProvider, Highlight } from "../useHighlight";
import { Code, Markdown } from "../Markdown";
import { attributeDoesMatchFilter } from "./utils";

const CATEGORY_ICONS: {
  [key: string]: string;
} = {
  "Command step": "heroicons/outline/command-line",
  "Wait step": "heroicons/outline/clock",
  "Block step": "heroicons/outline/pause-circle",
  "Input step": "heroicons/outline/arrow-down-circle",
  "Trigger step": "heroicons/outline/bolt",
  "Group step": "heroicons/outline/squares-2x2",
  "Triggering notifications": "heroicons/outline/bell-alert",
};

export function GuideList() {
  const { filter, content, onSelectItem } = useGuides();

  const matchedContent = useMemo(() => {
    if (!filter || !content) {
      return [];
    }

    return Object.values(content)
      .flatMap((contentCategory) => contentCategory.attributes)
      .flatMap((item) =>
        item.attributes
          .filter((attribute) => attributeDoesMatchFilter(attribute, filter))
          .map((attribute) => ({ item, attribute })),
      );
  }, [filter]);

  if (filter) {
    return (
      <List>
        <ListHeader name="Attributes" />

        {matchedContent.length ? (
          <HighlightProvider value={filter}>
            <ListContent>
              {matchedContent.map(({ item, attribute }) => (
                <ListItem
                  key={`${item.name}-${attribute.name}`}
                  onClick={() => onSelectItem(item, attribute)}
                  className="py-2"
                  name={
                    <>
                      <Highlight as="span" className="font-mono font-semibold">
                        {attribute.name}
                      </Highlight>
                      {" in "}
                      <Highlight>{item.name}</Highlight>
                    </>
                  }
                >
                  <Markdown
                    components={{
                      a: "span", // eslint-disable-line id-length
                      div: Highlight,
                      code: (props) => <Highlight as={Code} {...props} />,
                    }}
                  >
                    {attribute.textContent.split("\n").shift()}
                  </Markdown>
                </ListItem>
              ))}
            </ListContent>
          </HighlightProvider>
        ) : (
          <p className="m-0 text-charcoal-300">No matches found for “{filter}”.</p>
        )}
      </List>
    );
  }

  return (
    <>
      {content.map((category) => (
        <List key={category.name}>
          <ListHeader name={category.name} description={category.textContent} />

          <ListContent>
            {category.attributes.map((contentItem) => (
              <ListItem
                key={contentItem.name}
                onClick={() => onSelectItem(contentItem)}
                name={contentItem.name}
                icon={CATEGORY_ICONS[contentItem.name]}
              >
                <span className="line-clamp-2">{contentItem.shortDescription}</span>
              </ListItem>
            ))}
          </ListContent>
        </List>
      ))}
    </>
  );
}
